import React, { useState, useEffect, useRef } from 'react';
import './PrivateLessonForm.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { coachingGroupLabel } from '../constants';
import Calendar from './Calendar';

const PrivateLessonsForm = ({
  handleSportSelect,
  selectedSport,
  sports,
  serverToday,
  currentDate,
  maxBookingDate,
  cycleStartDate,
  skipSportSelection = false,
  externalSelectedDate = null,
}) => {
  const navigate = useNavigate();

  const [fetchedSports, setFetchedSports] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [visibleTooltip, setVisibleTooltip] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeslots, setSelectedTimeslots] = useState([]);
  const [coachBookedTimeslots, setCoachBookedTimeslots] = useState([]);
  const [coachOwnBookings, setCoachOwnBookings] = useState([]);
  const [courtFullTimeslots, setCourtFullTimeslots] = useState([]);
  const [isLoadingTimeslots, setIsLoadingTimeslots] = useState(false);
  const [pax, setPax] = useState(1);
  const [classType, setClassType] = useState('private'); // 'private' | 'group'
  const [perSlotPrice, setPerSlotPrice] = useState(0); // from coaching_booking_price RPC (single source of truth)
  const [priceRetryNonce, setPriceRetryNonce] = useState(0); // bumped to refetch the price after a failed RPC
  const [coachAvailability, setCoachAvailability] = useState([]); // [{ day_of_week, start_hour, end_hour }]
  const [coachHasSchedule, setCoachHasSchedule] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [error, setError] = useState(null);

  const isGroup = classType === 'group';
  // Customer-facing name for class_type 'group' — this coach's configured
  // label, else the sport default. Recomputed per coach, so the button renames
  // itself when the customer switches coaches.
  const groupLabel = coachingGroupLabel(selectedCoach?.groupLabel, selectedSport);
  const coachOffersGroup = !!selectedCoach?.offersGroup;
  const groupBlockSize = Math.max(1, selectedCoach?.groupBlockSize || 1);
  // Private: 1–2 players. Group: fixed at 4 players (not adjustable).
  const paxMin = isGroup ? 4 : 1;
  const paxMax = isGroup ? 4 : 2;

  const coachRefs = useRef({});
  const timeoutRef = useRef(null);
  const paxRef = useRef(null);

  // Sync external date (from App.js) into internal state when embedded.
  // Timeslots are date-scoped, so a real day change clears any selection made on
  // the previous day — before this, slots picked for Monday stayed highlighted
  // (and would be submitted) after switching the calendar to Tuesday. Coach and
  // class type survive the date change; they're not date-scoped. The ref guard
  // compares the DAY, not object identity, because re-picking the same date in
  // the calendar mints a fresh Date object.
  const prevExternalDayRef = useRef(null);
  useEffect(() => {
    if (externalSelectedDate) {
      setSelectedDate(externalSelectedDate);
      const day = externalSelectedDate.toDateString();
      if (prevExternalDayRef.current !== null && prevExternalDayRef.current !== day) {
        setSelectedTimeslots([]);
        setError(null);
      }
      prevExternalDayRef.current = day;
    }
  }, [externalSelectedDate]);

  // Fetch sports if not passed as props
  useEffect(() => {
    if (!sports) {
      const fetchSports = async () => {
        try {
          const { data, error } = await supabase
            .from('sports')
            .select('id, name')
            .order('name', { ascending: true });
          if (error) throw error;
          setFetchedSports(data || []);
        } catch (err) {
          setError('Failed to fetch sports: ' + err.message);
        }
      };
      fetchSports();
    }
  }, [sports]);

  // Fetch coaches when a sport is selected
  useEffect(() => {
    if (selectedSport) {
      const fetchCoaches = async () => {
        try {
          const { data, error } = await supabase
            .rpc('get_coaches', { sport_name: selectedSport });
          if (error) throw error;
          const mappedCoaches = (data || [])
            .filter(coach => coach.coach_display_name || coach.display_name || coach.username)
            .map(coach => ({
              id: coach.user_id,
              name: coach.coach_display_name || coach.display_name || coach.username,
              profilePicture: coach.avatar_url || 'https://placehold.co/100x100?text=Coach',
              description: coach.description,
              privateBasePrice: Number(coach.private_base_price) || 0,
              privateAddonPerPax: Number(coach.private_addon_per_pax) || 0,
              offersGroup: !!coach.offers_group,
              groupPrice: Number(coach.group_price) || 0,
              groupBlockSize: Math.max(1, parseInt(coach.group_block_size, 10) || 1),
              // Per-coach name for the group class, set by admin. Null = sport default.
              groupLabel: coach.group_label || null,
            }));
          setCoaches(mappedCoaches);
        } catch (err) {
          setError('Failed to fetch coaches: ' + err.message);
        }
      };
      fetchCoaches();
    } else {
      setCoaches([]);
    }
  }, [selectedSport]);

  // Fetch the coach's weekly availability timetable when the coach (or sport) changes.
  useEffect(() => {
    if (!selectedCoach || !selectedSport) {
      setCoachAvailability([]);
      setCoachHasSchedule(false);
      setIsLoadingSchedule(false);
      return;
    }
    let cancelled = false;
    // Clear the previous coach's schedule immediately so a stale timetable can't render
    // (and the grid is gated on isLoadingSchedule until the new one loads).
    setCoachAvailability([]);
    setCoachHasSchedule(false);
    setIsLoadingSchedule(true);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_coach_availability', {
          p_coach_id: selectedCoach.id,
          p_sport_name: selectedSport,
        });
        if (cancelled) return;
        if (error) throw error;
        setCoachAvailability(data || []);
        setCoachHasSchedule((data || []).length > 0);
      } catch (err) {
        if (!cancelled) {
          setCoachAvailability([]);
          setCoachHasSchedule(false);
        }
      } finally {
        if (!cancelled) setIsLoadingSchedule(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCoach, selectedSport]);

  // Fetch coach availability when coach or date changes
  useEffect(() => {
    if (selectedCoach && selectedDate) {
      fetchCoachAvailability(selectedCoach.id, selectedDate);
    } else {
      setCoachBookedTimeslots([]);
      setCoachOwnBookings([]);
    }
  }, [selectedCoach, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCoachAvailability = async (coachId, date) => {
    setIsLoadingTimeslots(true);
    try {
      const localDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
      const dateStr =
        localDate.getFullYear() +
        '-' +
        String(localDate.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(localDate.getDate()).padStart(2, '0');

      // 1. Fetch coach's coaching bookings (where coach is assigned via coach_bookings)
      const { data: bookedData, error } = await supabase
        .from('bookings')
        .select('timeslot, coach_bookings!inner(coach_id)')
        .eq('coach_bookings.coach_id', coachId)
        .eq('booking_date', dateStr);

      if (error) throw error;
      setCoachBookedTimeslots((bookedData || []).map(b => b.timeslot));

      // 2. Fetch coach's own court bookings (where coach is the booking user)
      const { data: coachOwnData, error: coachOwnError } = await supabase
        .from('bookings')
        .select('timeslot')
        .eq('user_id', coachId)
        .eq('booking_date', dateStr)
        .eq('booking_type', 'court_booking');

      if (coachOwnError) throw coachOwnError;
      setCoachOwnBookings((coachOwnData || []).map(b => b.timeslot));

      // 3. Check court availability per timeslot for this sport
      // Get sport_id
      const { data: sportData } = await supabase
        .from('sports')
        .select('id')
        .eq('name', selectedSport)
        .single();

      if (!sportData) {
        setCourtFullTimeslots([]);
        return;
      }

      // Get all active courts for this sport
      const { data: courts } = await supabase
        .from('courts')
        .select('id, court_types!inner(sport_id)')
        .eq('court_types.sport_id', sportData.id)
        .eq('is_active', true);

      if (!courts || courts.length === 0) {
        // No courts at all — all timeslots are full
        setCourtFullTimeslots(['all']);
        return;
      }

      // Get all bookings for these courts on this date
      const courtIds = courts.map(c => c.id);
      const { data: courtBookings } = await supabase
        .from('bookings')
        .select('court_id, timeslot')
        .in('court_id', courtIds)
        .eq('booking_date', dateStr);

      // For each timeslot, check if ALL courts are booked
      const courtBookingMap = {};
      (courtBookings || []).forEach(b => {
        if (!courtBookingMap[b.timeslot]) courtBookingMap[b.timeslot] = new Set();
        courtBookingMap[b.timeslot].add(b.court_id);
      });

      // A timeslot is "full" when every court for this sport is already booked at it.
      // Derived directly from actual bookings, so it covers any schedule (incl. late-night).
      const fullSlots = [];
      for (const ts of Object.keys(courtBookingMap)) {
        if (courtBookingMap[ts].size >= courts.length) {
          fullSlots.push(ts);
        }
      }
      setCourtFullTimeslots(fullSlots);
    } catch (err) {
      setCoachBookedTimeslots([]);
      setCoachOwnBookings([]);
      setCourtFullTimeslots([]);
    } finally {
      setIsLoadingTimeslots(false);
    }
  };

  const handleSportClick = (sport) => {
    if (selectedSport === sport) {
      handleSportSelect(null);
    } else {
      handleSportSelect(sport);
    }
    setSelectedDate(null);
    setSelectedCoach(null);
    setSelectedTimeslots([]);
    setPax(1);
    setCoachBookedTimeslots([]);
    setCoachOwnBookings([]);
    setCourtFullTimeslots([]);
    setVisibleTooltip(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedCoach(null);
    setSelectedTimeslots([]);
    setPax(1);
    setClassType('private');
    setError(null);
    setCoachBookedTimeslots([]);
    setCoachOwnBookings([]);
    setCourtFullTimeslots([]);
  };

  const handleCoachSelect = (coach) => {
    // Re-tapping the selected coach only re-shows the tooltip (the same click
    // handler calls showTooltip). It must NOT deselect: deselecting silently
    // reset class type to Private, so a customer who tapped their coach again
    // to read the bio then clicked a slot got single-slot selection despite
    // having chosen the group class — the "only 1 slot highlights" bug.
    // The old deselect+reselect also served as an accidental retry when the
    // price RPC had failed (perSlotPrice stuck at 0, checkout dead) — keep
    // that recovery deliberately: a re-tap refetches the price, nothing else.
    if (selectedCoach && selectedCoach.id === coach.id) {
      setPriceRetryNonce((n) => n + 1);
      return;
    }
    setSelectedCoach(coach);
    setSelectedTimeslots([]);
    // Keep the chosen class type across a coach switch when the new coach also
    // offers group sessions; otherwise fall back to private. Pax snaps to the
    // mode (group is fixed at 4, private starts at 1).
    const keepGroup = classType === 'group' && !!coach.offersGroup;
    setClassType(keepGroup ? 'group' : 'private');
    setPax(keepGroup ? 4 : 1);
    setError(null);
  };

  // Per-slot price from the single source of truth (coaching_booking_price RPC).
  // Re-fetched whenever the coach / class type / pax changes; everything that shows
  // or sends a price derives from this so the formula lives in exactly one place.
  useEffect(() => {
    if (!selectedCoach || !selectedSport) {
      setPerSlotPrice(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('coaching_booking_price', {
          p_coach_id: selectedCoach.id,
          p_sport_name: selectedSport,
          p_class_type: classType,
          p_pax: pax,
        });
        if (cancelled) return;
        if (error) throw error;
        setPerSlotPrice(Number(data) || 0);
      } catch (err) {
        if (!cancelled) {
          setPerSlotPrice(0);
          // Surface the failure — a silent 0 leaves "RM 0.00" and a disabled
          // checkout button with no explanation and no visible way to recover.
          setError("Couldn't load the coach's price. Tap the coach again to retry.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCoach, selectedSport, classType, pax, priceRetryNonce]);

  const handleClassTypeSelect = (type) => {
    if (classType === type) return; // no-op if clicking the already-selected type
    // Snap pax when switching: group is fixed at 4, private resets to 1.
    // (Side effects live OUT here, not inside a setClassType updater — updaters
    // must be pure; StrictMode double-invokes them.)
    setPax(type === 'group' ? 4 : 1);
    // Clear any selected timeslots — selection model differs between Private (single) and Group (block)
    setSelectedTimeslots([]);
    setError(null);
    setClassType(type);
  };

  const showTooltip = (coachId, e) => {
    e.stopPropagation();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setVisibleTooltip(coachId);
    timeoutRef.current = setTimeout(() => {
      setVisibleTooltip(null);
    }, 2000);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (visibleTooltip !== null) {
        const coachRef = coachRefs.current[visibleTooltip];
        if (coachRef && !coachRef.contains(e.target)) {
          setVisibleTooltip(null);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [visibleTooltip]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCheckout = () => {
    const localDate = new Date(selectedDate.getTime() + 8 * 60 * 60 * 1000);
    const bookingDate =
      localDate.getFullYear() +
      '-' +
      String(localDate.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(localDate.getDate()).padStart(2, '0');

    navigate('/checkout-coaching', {
      state: {
        selectedSport,
        selectedCoach,
        selectedDate,
        selectedTimeslots,
        pax,
        bookingDate,
        selectedBookingType: 'private-lesson',
        classType,
        perSlotPrice,
      },
    });
  };

  const displaySports = sports || fetchedSports;
  const filteredSports = displaySports
    .filter(sport => ['Pickle', 'Padel'].includes(sport.name))
    .sort((a, b) => {
      if (a.name === 'Pickle') return -1;
      if (b.name === 'Pickle') return 1;
      return 0;
    });

  // Pricing derives entirely from perSlotPrice (the coaching_booking_price RPC value).
  //   perSlotPrice = price per timeslot/booking row.
  //   Group session price (for display) = perSlotPrice × group_block_size = group_price.
  const numSessions = isGroup
    ? Math.floor(selectedTimeslots.length / groupBlockSize)
    : selectedTimeslots.length;
  const pricePerSession = isGroup ? perSlotPrice * groupBlockSize : perSlotPrice;
  const totalPrice = pricePerSession * numSessions;

  // Hour index (0..27, measured from midnight of the booking day; 24=12am next day,
  // 25=1am, 26=2am) -> stored timeslot string "HH:MM AM/PM" and a human label.
  const hourToTimeStr = (h) => {
    const hh = ((h % 24) + 24) % 24;
    const period = hh < 12 ? 'AM' : 'PM';
    let disp = hh % 12;
    if (disp === 0) disp = 12;
    return `${String(disp).padStart(2, '0')}:00 ${period}`;
  };
  const hourToLabel = (h) => {
    const fmt = (x) => {
      const hh = ((x % 24) + 24) % 24;
      const p = hh < 12 ? 'AM' : 'PM';
      let d = hh % 12;
      if (d === 0) d = 12;
      return `${d}${p}`;
    };
    return `${fmt(h)} - ${fmt(h + 1)}`;
  };

  // Generate coaching timeslots from the selected coach's weekly availability schedule.
  // Each slot is 1 hour. Past slots and slots inside the 24-hour minimum lead time are
  // marked unavailable (folded into isPast so the existing rendering disables them).
  const coachingSlots = (() => {
    if (!selectedDate) return [];
    const now = new Date();
    const nowMs = now.getTime();
    const LEAD_MS = 24 * 60 * 60 * 1000; // 24-hour minimum booking lead time
    const dow = selectedDate.getDay(); // 0=Sunday .. 6=Saturday

    // Resolve the bookable hour windows for this day from the coach's schedule.
    // Fall back to the legacy defaults only if the coach has no schedule rows at all.
    let windows;
    if (coachHasSchedule) {
      // Skip days flagged off (is_off); an off day yields no bookable windows.
      windows = coachAvailability
        .filter((w) => w.day_of_week === dow && !w.is_off)
        .map((w) => ({ start: w.start_hour, end: w.end_hour }));
    } else if (dow === 0) {
      windows = []; // Sunday closed
    } else if (dow === 6) {
      windows = [{ start: 9, end: 14 }]; // Saturday 9am-2pm
    } else {
      windows = [{ start: 9, end: 21 }]; // Weekday 9am-9pm
    }
    if (!windows || windows.length === 0) return [];

    // Union of hour indices across all windows (supports split shifts), sorted.
    const hourSet = new Set();
    for (const w of windows) {
      for (let h = w.start; h < w.end; h++) hourSet.add(h);
    }
    const hours = [...hourSet].sort((a, b) => a - b);

    const baseMidnight = new Date(
      selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0, 0
    );
    return hours.map((h) => {
      const time = hourToTimeStr(h);
      const label = hourToLabel(h);
      const slotStart = new Date(baseMidnight.getTime());
      slotStart.setHours(slotStart.getHours() + h); // h>=24 rolls into the next day
      const slotStartMs = slotStart.getTime();
      const slotEndMs = slotStartMs + 60 * 60 * 1000;
      const isPast = slotEndMs <= nowMs;
      const isTooSoon = slotStartMs < nowMs + LEAD_MS; // 24-hour minimum lead time
      const isBooked = coachBookedTimeslots.includes(time);
      const isCoachOwnBooking = coachOwnBookings.includes(time);
      const isCourtFull = courtFullTimeslots.includes('all') || courtFullTimeslots.includes(time);
      return { hour: h, time, label, isPast: isPast || isTooSoon, isBooked, isCoachOwnBooking, isCourtFull };
    });
  })();

  // Slot click handler. For block_size=1 (default) this is a plain toggle.
  // For block_size>1, we add/remove a consecutive block of slots starting at the clicked one.
  const handleSlotToggle = (slotTime) => {
    const slotIndex = coachingSlots.findIndex(s => s.time === slotTime);
    if (slotIndex === -1) return;

    if (groupBlockSize <= 1 || !isGroup) {
      setSelectedTimeslots(prev =>
        prev.includes(slotTime) ? prev.filter(t => t !== slotTime) : [...prev, slotTime]
      );
      return;
    }

    // Removing: find the start of the consecutive selected run containing slotIndex,
    // then snap to the block-aligned start that includes slotIndex.
    if (selectedTimeslots.includes(slotTime)) {
      let runStart = slotIndex;
      while (
        runStart > 0 &&
        selectedTimeslots.includes(coachingSlots[runStart - 1]?.time)
      ) {
        runStart--;
      }
      const offset = Math.floor((slotIndex - runStart) / groupBlockSize) * groupBlockSize;
      const blockStart = runStart + offset;
      const blockTimes = [];
      for (let i = 0; i < groupBlockSize; i++) {
        const s = coachingSlots[blockStart + i];
        if (s) blockTimes.push(s.time);
      }
      setSelectedTimeslots(prev => prev.filter(t => !blockTimes.includes(t)));
      setError(null);
      return;
    }

    // Adding: validate that all block_size CLOCK-CONSECUTIVE slots from slotIndex are
    // available. Array adjacency is not enough — a split shift (e.g. 9-12, 14-17) leaves
    // a gap, so we require each slot's hour to be exactly one more than the previous.
    const startHour = coachingSlots[slotIndex].hour;
    const block = [];
    for (let i = 0; i < groupBlockSize; i++) {
      const s = coachingSlots[slotIndex + i];
      if (!s || s.hour !== startHour + i) {
        setError(`A ${groupBlockSize}-slot session needs ${groupBlockSize} back-to-back hours, which aren't available starting here.`);
        return;
      }
      if (s.isPast || s.isBooked || s.isCoachOwnBooking || s.isCourtFull) {
        setError(`Cannot start a session here — ${s.label} is unavailable.`);
        return;
      }
      if (selectedTimeslots.includes(s.time)) {
        setError(`Cannot start a session here — ${s.label} overlaps another selected session.`);
        return;
      }
      block.push(s.time);
    }
    setSelectedTimeslots(prev => [...prev, ...block]);
    setError(null);
  };

  // Disable Sundays in the calendar
  const disabledDays = (() => {
    const days = [];
    const start = currentDate || new Date();
    const end = maxBookingDate || (() => { const d = new Date(); d.setDate(d.getDate() + 14); return d; })();
    const d = new Date(start);
    while (d <= end) {
      if (d.getDay() === 0) { // Sunday
        days.push(new Date(d));
      }
      d.setDate(d.getDate() + 1);
    }
    return days;
  })();

  const minDate = currentDate || new Date();
  const maxDate = maxBookingDate || (() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d;
  })();

  const stepOffset = skipSportSelection ? 1 : 0;

  // When embedded in Bookings page, only render the right column (confirm-booking)
  // Date is handled by App.js in the left column (selection-panel)
  if (skipSportSelection) {
    return (
      <div className="confirm-booking">
        <h2 className="step-title">3. Coach</h2>
          <div className="coach-buttons">
            {coaches.length > 0 ? (
              coaches.map(coach => (
                <button
                  key={coach.id}
                  className={`coach-button program-tooltip ${selectedCoach?.id === coach.id ? 'selected' : ''}`}
                  ref={el => (coachRefs.current[coach.id] = el)}
                  onClick={e => {
                    handleCoachSelect(coach);
                    showTooltip(coach.id, e);
                  }}
                >
                  <img src={coach.profilePicture} alt={coach.name} className="coach-image" />
                  <p className="coach-name">{coach.name}</p>
                  {coach.description && (
                    <span
                      className={`program-tooltiptext ${visibleTooltip === coach.id ? 'visible' : ''}`}
                    >
                      {coach.description}
                    </span>
                  )}
                </button>
              ))
            ) : (
              <p>No coaches available for this sport.</p>
            )}
          </div>

          {selectedCoach && coachOffersGroup && (
            <>
              <h2 className="step-title">4. Class Type</h2>
              <div className="category-buttons">
                <button
                  onClick={() => handleClassTypeSelect('private')}
                  aria-pressed={classType === 'private'}
                  className={`category-button class-type-button ${classType === 'private' ? 'active' : ''}`}
                >
                  Private Class · RM {selectedCoach.privateBasePrice}
                </button>
                <button
                  onClick={() => handleClassTypeSelect('group')}
                  aria-pressed={classType === 'group'}
                  className={`category-button class-type-button ${classType === 'group' ? 'active' : ''}`}
                >
                  {groupLabel} · RM {selectedCoach.groupPrice} ({groupBlockSize}-slot session)
                </button>
              </div>
            </>
          )}

          {selectedCoach && (
            <>
              <h2 className="step-title">{coachOffersGroup ? '5' : '4'}. Timeslot</h2>
              {/* Block hint: only when the grid is actually showing (not while
                  loading, not on a closed day), and deliberately WITHOUT the
                  admin label — a label like "… 90 minutes" composed into a
                  sentence about hourly slots reads as a contradiction. */}
              {isGroup && groupBlockSize > 1 && !isLoadingTimeslots && !isLoadingSchedule &&
                coachingSlots.length > 0 && (
                <p className="group-slot-hint">
                  Tap a start time — this books {groupBlockSize} back-to-back slots as one session.
                </p>
              )}
              {(isLoadingTimeslots || isLoadingSchedule) ? (
                <p className="loading-timeslots">Checking coach availability...</p>
              ) : coachingSlots.length === 0 ? (
                <p className="loading-timeslots">Coach is not available on this day. Please pick another date.</p>
              ) : (
                <div className="coaching-timeslot-grid">
                  {coachingSlots.map(slot => {
                    const isSelected = selectedTimeslots.includes(slot.time);
                    const isUnavailable = slot.isBooked || slot.isCoachOwnBooking || slot.isCourtFull;
                    let slotClass = 'coaching-timeslot-button';
                    if (isUnavailable) slotClass += ' booked';
                    else if (slot.isPast) slotClass += ' past';
                    else if (isSelected) slotClass += ' selected';
                    return (
                      <button
                        key={slot.hour}
                        className={slotClass}
                        onClick={() => {
                          if (isUnavailable || slot.isPast) return;
                          handleSlotToggle(slot.time);
                        }}
                        disabled={isUnavailable || slot.isPast}
                      >
                        {slot.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {error && <p style={{ color: '#d32f2f', marginTop: '8px' }}>{error}</p>}

              {selectedTimeslots.length > 0 && numSessions > 0 && (
                <>
                  <h2 className="step-title">
                    {coachOffersGroup ? '6' : '5'}. Players
                    {!isGroup && selectedCoach.privateAddonPerPax > 0 && ` · Additional RM ${selectedCoach.privateAddonPerPax} per pax`}
                    {isGroup ? ` • Suitable for 3-4 pax` : ` • Suitable for 1-2 pax`}
                  </h2>
                  <div className="pax-selector">
                    <button
                      className="pax-button"
                      onClick={() => setPax(Math.max(paxMin, pax - 1))}
                      disabled={pax <= paxMin}
                    >
                      -
                    </button>
                    <span className="pax-count">{pax}</span>
                    <button
                      className="pax-button"
                      onClick={() => setPax(Math.min(paxMax, pax + 1))}
                      disabled={pax >= paxMax}
                    >
                      +
                    </button>
                  </div>
                  <p className="price-display">
                    RM {pricePerSession.toFixed(2)} x {numSessions} session{numSessions > 1 ? 's' : ''} = RM {totalPrice.toFixed(2)}
                  </p>
                  <button
                    className="checkout-button"
                    onClick={handleCheckout}
                    disabled={perSlotPrice <= 0 || (isGroup && selectedTimeslots.length % groupBlockSize !== 0)}
                  >
                    Proceed to Checkout
                  </button>
                </>
              )}
            </>
          )}
      </div>
    );
  }

  return (
    <>
      {/* Step 1: Sport */}
      <div className="selection-panel">
        <h2 className="step-title">1. Sport</h2>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        <div className="category-buttons">
          {filteredSports.length > 0 ? (
            filteredSports.map(sport => (
              <button
                key={sport.id}
                onClick={() => handleSportClick(sport.name)}
                className={`category-button ${selectedSport === sport.name ? 'active' : ''}`}
              >
                {sport.name}
              </button>
            ))
          ) : (
            <p>Loading sports...</p>
          )}
        </div>

        {/* Date */}
        {selectedSport && (
          <>
            <h2 className="step-title">2. Date</h2>
            <Calendar
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              disabledDays={disabledDays}
              minDate={minDate}
              maxDate={maxDate}
              selectedSport={selectedSport}
            />
          </>
        )}
      </div>

      {/* Step 3: Coach + Step 4: Timeslot (same panel) */}
      {selectedSport && selectedDate && (
        <div className="confirm-booking">
          <h2 className="step-title">{3 - stepOffset}. Coach</h2>
          <div className="coach-buttons">
            {coaches.length > 0 ? (
              coaches.map(coach => (
                <button
                  key={coach.id}
                  className={`coach-button program-tooltip ${selectedCoach?.id === coach.id ? 'selected' : ''}`}
                  ref={el => (coachRefs.current[coach.id] = el)}
                  onClick={e => {
                    handleCoachSelect(coach);
                    showTooltip(coach.id, e);
                  }}
                >
                  <img src={coach.profilePicture} alt={coach.name} className="coach-image" />
                  <p className="coach-name">{coach.name}</p>
                  {coach.description && (
                    <span
                      className={`program-tooltiptext ${visibleTooltip === coach.id ? 'visible' : ''}`}
                    >
                      {coach.description}
                    </span>
                  )}
                </button>
              ))
            ) : (
              <p>No coaches available for this sport.</p>
            )}
          </div>

          {selectedCoach && (
            <>
              <h2 className="step-title">{4 - stepOffset}. Timeslot</h2>
              {(isLoadingTimeslots || isLoadingSchedule) ? (
                <p className="loading-timeslots">Checking coach availability...</p>
              ) : coachingSlots.length === 0 ? (
                <p className="loading-timeslots">Coach is not available on this day. Please pick another date.</p>
              ) : (
                <div className="coaching-timeslot-grid">
                  {coachingSlots.map(slot => {
                    const isSelected = selectedTimeslots.includes(slot.time);
                    const isUnavailable = slot.isBooked || slot.isCoachOwnBooking || slot.isCourtFull;
                    let slotClass = 'coaching-timeslot-button';
                    if (isUnavailable) slotClass += ' booked';
                    else if (slot.isPast) slotClass += ' past';
                    else if (isSelected) slotClass += ' selected';
                    return (
                      <button
                        key={slot.hour}
                        className={slotClass}
                        onClick={() => {
                          if (isUnavailable || slot.isPast) return;
                          setSelectedTimeslots(prev =>
                            prev.includes(slot.time)
                              ? prev.filter(t => t !== slot.time)
                              : [...prev, slot.time]
                          );
                        }}
                        disabled={isUnavailable || slot.isPast}
                      >
                        {slot.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedTimeslots.length > 0 && (
                <>
                  <h2 className="step-title">{5 - stepOffset}. Players</h2>
                  <div className="pax-selector">
                    <button
                      className="pax-button"
                      onClick={() => setPax(Math.max(1, pax - 1))}
                      disabled={pax <= 1}
                    >
                      -
                    </button>
                    <span className="pax-count">{pax}</span>
                    <button
                      className="pax-button"
                      onClick={() => setPax(pax + 1)}
                    >
                      +
                    </button>
                  </div>
                  <p className="price-display">
                    RM {pricePerSession}.00 x {selectedTimeslots.length} session{selectedTimeslots.length > 1 ? 's' : ''} = RM {totalPrice}.00
                  </p>
                  <button
                    className="checkout-button"
                    onClick={handleCheckout}
                  >
                    Proceed to Checkout
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default PrivateLessonsForm;
